import { IconAlert } from './icons.js';
import { Modal } from './Modal.js';
import modal from './Modal.module.css';
import styles from './ConfirmDialog.module.css';

/**
 * A modal confirmation for actions that cannot be undone.
 *
 * The shell, its layout and all of its keyboard behaviour come from `Modal` —
 * this only supplies the alert mark, the wording and the two buttons.
 *
 * Cancel comes first in the DOM, and that is deliberate rather than incidental:
 * `Modal` focuses the first focusable thing it contains, so Cancel takes focus on
 * open. This component exists to interrupt a destructive action, so a reflexive
 * Enter should back out rather than go through with it.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  confirmIcon,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  confirmIcon?: React.ReactNode;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      labelledBy="confirm-title"
      footer={
        <>
          <button type="button" className={modal.ghost} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={modal.danger} onClick={onConfirm} disabled={busy}>
            {confirmIcon}
            {busy ? 'Removing…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className={styles.body}>
        <span className={modal.mark} aria-hidden="true">
          <IconAlert width="17" height="17" />
        </span>
        <div className={styles.text}>{body}</div>
      </div>
    </Modal>
  );
}
